define('ArtworkUpload.EntryPoint', [
    'ArtworkUpload.PDP.View',
    'ArtworkUpload.PendingRegistry',
    'LiveOrder.Model',
    'Configuration',
    'jQuery',
    'underscore',
    'artwork_see_image_trigger.tpl'
], function (
    ArtworkUploadPDPView,
    ArtworkUploadPendingRegistry,
    LiveOrderModel,
    Configuration,
    jQuery,
    _,
    artwork_see_image_trigger_tpl
) {
    'use strict';

    var CONFIG = Configuration.ArtworkUpload || {};
    var CART_OPTION_ID = CONFIG.cartOptionId || 'custcol_artwork_file';
    var OVERLAY_ID = 'artwork-see-image-overlay';

    // Feature flags (Site Management Tools > ArtworkUpload). Both default to
    // enabled (`!== false`) so an unconfigured/missing property doesn't turn
    // the feature off. Each part is independently toggleable so either half
    // can be rolled back without touching the other (e.g. keep "See Image"
    // working for orders placed while the PDP widget was on, even after
    // turning the widget itself off).
    var ENABLE_PDP_WIDGET = CONFIG.enablePdpWidget !== false;
    var ENABLE_CART_SEE_IMAGE = CONFIG.enableCartSeeImage !== false;

    // NetSuite's line-option wire format expects value as an
    // {internalid, label} object regardless of the option's own type (list
    // or free text) - confirmed by CustomPLP.AddToCart.View.js's working
    // custcol20 (Light Fixture, also free text) call. A raw/JSON-stringified
    // scalar here gets silently dropped server-side instead of persisted.
    //
    // For a free-text option NetSuite only reliably keeps `internalid` -
    // `label` comes back mirroring whatever we sent as internalid (no
    // separate label storage exists for a non-list option). Multiple files
    // are packed into that single string: url/name joined per file with the
    // unit separator, and files joined together with the record separator.
    var FIELD_DELIMITER = String.fromCharCode(31); // ASCII unit separator (0x1F) - between url and name
    var ITEM_DELIMITER = String.fromCharCode(30); // ASCII record separator (0x1E) - between files

    function encodeArtworkValue(artworks) {
        var packed = _.map(artworks, function (a) {
            return a.fileUrl + FIELD_DELIMITER + a.fileName;
        }).join(ITEM_DELIMITER);
        return { internalid: packed, label: packed };
    }

    function decodeArtworkValue(rawValue) {
        var packed = null;
        if (_.isObject(rawValue)) {
            var obj = rawValue.attributes || rawValue;
            packed = obj.internalid || obj.label || null;
        } else if (typeof rawValue === 'string') {
            packed = rawValue;
        }
        if (!packed) return [];

        var records = packed.split(ITEM_DELIMITER);
        var artworks = _.compact(_.map(records, function (record) {
            var parts = record.split(FIELD_DELIMITER);
            return parts.length === 2 ? { url: parts[0], name: parts[1] } : null;
        }));
        if (artworks.length) return artworks;

        // Fallback for values persisted before this encoding existed
        // (older JSON-string shape, or a bare URL).
        try {
            var parsed = JSON.parse(packed);
            if (parsed && parsed.url) return [parsed];
        } catch (ex) {
            // Not JSON either.
        }
        return [{ url: packed, name: 'Artwork' }];
    }

    function getItemInternalId(line) {
        if (!line) return null;
        // Native PDP add-to-cart passes a full Transaction.Line Backbone
        // Model (line.get('item') is itself a Model), not the plain
        // {item:{internalid}} object CustomPLP builds by hand for its own
        // bypass flow - support both shapes.
        var itemRef = _.isFunction(line.get) ? line.get('item') : line.item;
        if (!itemRef) return null;
        return _.isFunction(itemRef.get) ? itemRef.get('internalid') : itemRef.internalid;
    }

    // The line's `options` is a Backbone.Collection of option models (one per
    // Item Option configured on the item) when it comes from the native
    // add-to-cart flow, or a plain array when built by hand (e.g. a future
    // CustomPLP-style bypass) - normalize lookup across both shapes.
    function findArtworkOptionModel(line) {
        if (!line) return null;

        if (_.isFunction(line.get)) {
            var options = line.get('options');
            var optionModels = options && options.models ? options.models : options;
            return (optionModels && _.find(optionModels, function (o) {
                var cartOptionId = _.isFunction(o.get) ? o.get('cartOptionId') : (o.attributes ? o.attributes.cartOptionId : o.cartOptionId);
                return cartOptionId === CART_OPTION_ID;
            })) || null;
        }

        if (Array.isArray(line.options)) {
            return _.find(line.options, function (o) {
                return (o.cartOptionId || (o.attributes && o.attributes.cartOptionId)) === CART_OPTION_ID;
            }) || null;
        }

        return null;
    }

    // An item requiring artwork is one that has this option configured at
    // all (same signal the PDP widget uses to decide whether to show
    // itself) - used to block add-to-cart when nothing was uploaded.
    function lineRequiresArtwork(line) {
        return !!findArtworkOptionModel(line);
    }

    function setLineArtworkOption(line, encodedValue) {
        var optionModel = findArtworkOptionModel(line);

        if (optionModel && _.isFunction(optionModel.set)) {
            optionModel.set('value', encodedValue);
            return true;
        }

        // Fallback for a plain-object line shape with no pre-built option
        // slot (e.g. a future bypass flow built the way
        // CustomPLP.AddToCart.View constructs its lines).
        if (line && Array.isArray(line.options)) {
            line.options = line.options.concat([{ cartOptionId: CART_OPTION_ID, value: encodedValue }]);
            return true;
        }

        return false;
    }

    // --- Part D: attach the pending PDP upload(s) to whichever line is being
    // added, and block the add entirely if the item requires artwork but
    // none was uploaded. LiveOrder.Model.addLine is the single choke point
    // every add-to-cart action (native PDP button included) funnels through,
    // so patching it here covers native PDP add-to-cart without needing to
    // know its internal module id (not vendored/inspectable in this repo).
    if (ENABLE_PDP_WIDGET) {
        _.extend(LiveOrderModel.prototype, {
            addLine: _.wrap(LiveOrderModel.prototype.addLine, function (fn, line) {
                var itemId = getItemInternalId(line);
                var pendingArtworks = itemId ? ArtworkUploadPendingRegistry.getAll(itemId) : [];
                var requiresArtwork = lineRequiresArtwork(line);

                if (requiresArtwork && !pendingArtworks.length) {
                    var message = 'Artwork required';
                    var activeWidget = itemId && ArtworkUploadPendingRegistry.getActiveWidget(itemId);
                    if (activeWidget && _.isFunction(activeWidget.showBlockedError)) {
                        activeWidget.showBlockedError(message);
                    }
                    var rejected = jQuery.Deferred();
                    rejected.reject({ responseJSON: { errorMessage: message } });
                    return rejected.promise();
                }

                if (pendingArtworks.length) {
                    setLineArtworkOption(line, encodeArtworkValue(pendingArtworks));
                    ArtworkUploadPendingRegistry.clear(itemId);
                }

                var rest = Array.prototype.slice.call(arguments, 2);
                return fn.apply(this, [line].concat(rest));
            })
        });
    }

    // --- Part E: cart / confirmation "See Image" trigger + slider.
    //
    // Cart line options are rendered by a core, generic options view (its
    // module id isn't vendored in this repo, only its template markup is:
    // `[data-view="Item.SelectedOptions"]` in cart_lines.tpl and
    // `[data-view="Line.SelectedOptions"]` in cart_confirmation_modal.tpl).
    // Rather than guess at that module id, we watch the DOM for those
    // containers and, once we can resolve the corresponding line's options
    // from LiveOrder.Model, inject our own "See Image" trigger next to
    // whatever the native renderer already put there.
    function findArtworksForLine(lineInternalId) {
        if (!lineInternalId) return null;

        var cart = LiveOrderModel.getInstance();
        var linesRaw = (cart && (cart.get('lines') || cart.get('items'))) || [];
        var lines = linesRaw.models ? linesRaw.models : linesRaw;

        var line = _.find(lines, function (l) {
            var attrs = (l && l.toJSON) ? l.toJSON() : l;
            return attrs && String(attrs.internalid) === String(lineInternalId);
        });
        if (!line) return null;

        var attrs = line.toJSON ? line.toJSON() : line;
        var options = attrs.options || [];
        var artworkOption = _.find(options, function (opt) {
            var o = (opt && opt.attributes) ? opt.attributes : opt;
            return o && o.cartOptionId === CART_OPTION_ID;
        });
        if (!artworkOption) return null;

        var normalized = artworkOption.attributes || artworkOption;
        var decoded = decodeArtworkValue(normalized.value);
        if (!decoded || !decoded.length) return null;

        return _.map(decoded, function (d) {
            return {
                fileUrl: d.url,
                fileName: d.name,
                // SVG excluded - stored as PLAINTEXT (no native NetSuite SVG
                // type), served with a text Content-Type an <img> won't render.
                isImage: /\.(jpg|jpeg|png)$/i.test(d.name || '')
            };
        });
    }

    function getLineInternalIdFromDom($container) {
        // Confirmed live markup: cart_lines.tpl's row root is
        // `<div id="item8850set650684" data-item-id="8850"
        // data-type="order-item" class="cart-lines-row">` - the line id lives
        // in the row's `id` attribute, NOT a data-internalid attribute (no
        // ancestor of Item.SelectedOptions carries one; data-internalid only
        // appears on unrelated sibling branches like the Remove/Save-for-later
        // links). Try that first, then fall back to data-internalid in case
        // the confirmation-modal markup (Line.SelectedOptions) differs.
        var $orderItemRoot = $container.closest('[data-type="order-item"]');
        if ($orderItemRoot.length && $orderItemRoot.attr('id')) return $orderItemRoot.attr('id');

        var $dataIdRoot = $container.closest('[data-internalid]');
        if ($dataIdRoot.length) return $dataIdRoot.attr('data-internalid');

        return null;
    }

    function injectSeeImageTrigger($container) {
        if (!$container.length || $container.data('artworkChecked')) return;
        $container.data('artworkChecked', true);

        var lineInternalId = getLineInternalIdFromDom($container);
        if (!lineInternalId) return;

        try {
            var artworks = findArtworksForLine(lineInternalId);
            if (!artworks || !artworks.length) return;

            var html = artwork_see_image_trigger_tpl({
                artworksJson: JSON.stringify(artworks),
                count: artworks.length,
                isMultiple: artworks.length > 1
            });
            $container.append(html);
        } catch (ex) {
            // Defensive: never break cart rendering if line/model shape differs
            // from what we assumed above.
            if (window.console) window.console.warn('ArtworkUpload: could not render See Image trigger', ex);
        }
    }

    function scanForOptionContainers($scope) {
        $scope.find('[data-view="Item.SelectedOptions"], [data-view="Line.SelectedOptions"]').each(function () {
            injectSeeImageTrigger(jQuery(this));
        });
    }

    var overlayState = { artworks: [], index: 0 };

    function renderOverlaySlide() {
        var artwork = overlayState.artworks[overlayState.index];
        if (!artwork) return '';

        var slide = artwork.isImage
            ? '<img src="' + _.escape(artwork.fileUrl) + '" alt="' + _.escape(artwork.fileName) + '" class="artwork-see-image-overlay-img">'
            : '<div class="artwork-see-image-overlay-file">' +
                '<p>' + _.escape(artwork.fileName) + '</p>' +
                '<a href="' + _.escape(artwork.fileUrl) + '" target="_blank" rel="noopener">Download / Open</a>' +
              '</div>';

        var nav = overlayState.artworks.length > 1
            ? '<div class="artwork-see-image-overlay-nav">' +
                '<button type="button" class="artwork-see-image-overlay-prev" data-action="artwork-see-image-prev">&#8249;</button>' +
                '<span class="artwork-see-image-overlay-counter">' + (overlayState.index + 1) + ' / ' + overlayState.artworks.length + '</span>' +
                '<button type="button" class="artwork-see-image-overlay-next" data-action="artwork-see-image-next">&#8250;</button>' +
              '</div>'
            : '';

        return '<div class="artwork-see-image-overlay-slide">' + slide + nav + '</div>';
    }

    function updateOverlaySlide() {
        jQuery('#' + OVERLAY_ID + ' .artwork-see-image-overlay-content > .artwork-see-image-overlay-slide').replaceWith(renderOverlaySlide());
    }

    function openOverlay(artworks) {
        jQuery('#' + OVERLAY_ID).remove();
        overlayState.artworks = artworks;
        overlayState.index = 0;

        var $overlay = jQuery(
            '<div id="' + OVERLAY_ID + '" class="artwork-see-image-overlay">' +
                '<div class="artwork-see-image-overlay-backdrop" data-action="artwork-see-image-close"></div>' +
                '<div class="artwork-see-image-overlay-content">' +
                    '<button type="button" class="artwork-see-image-overlay-close" data-action="artwork-see-image-close">&times;</button>' +
                    renderOverlaySlide() +
                '</div>' +
            '</div>'
        );

        jQuery('body').append($overlay);
    }

    // Everything below only wires up DOM listeners / observes the page when
    // the cart-side feature is turned on.
    if (ENABLE_CART_SEE_IMAGE) {
        jQuery(document).on('click', '[data-action="artwork-see-image"]', function (e) {
            e.preventDefault();
            var $trigger = jQuery(this);
            try {
                var artworks = JSON.parse($trigger.attr('data-artworks') || '[]');
                if (artworks.length) openOverlay(artworks);
            } catch (ex) {
                if (window.console) window.console.warn('ArtworkUpload: could not parse artworks for overlay', ex);
            }
        });

        jQuery(document).on('click', '[data-action="artwork-see-image-prev"]', function () {
            if (!overlayState.artworks.length) return;
            overlayState.index = (overlayState.index - 1 + overlayState.artworks.length) % overlayState.artworks.length;
            updateOverlaySlide();
        });

        jQuery(document).on('click', '[data-action="artwork-see-image-next"]', function () {
            if (!overlayState.artworks.length) return;
            overlayState.index = (overlayState.index + 1) % overlayState.artworks.length;
            updateOverlaySlide();
        });

        jQuery(document).on('click', '[data-action="artwork-see-image-close"]', function () {
            jQuery('#' + OVERLAY_ID).remove();
        });

        jQuery(document).on('keyup', function (e) {
            if (e.key === 'Escape' || e.keyCode === 27) jQuery('#' + OVERLAY_ID).remove();
        });

        if (window.MutationObserver) {
            var observer = new MutationObserver(function () {
                scanForOptionContainers(jQuery('body'));
            });
            observer.observe(document.body, { childList: true, subtree: true });
        }
    }

    return {
        mountToApp: function mountToApp(container) {
            if (!ENABLE_PDP_WIDGET) return;

            var pdp = container.getComponent('PDP');
            if (pdp) {
                pdp.addChildView('cms:item_info', function () {
                    return new ArtworkUploadPDPView({
                        container: container,
                        iteminfo: pdp.getItemInfo()
                    });
                });
            }
        }
    };
});
